// Utility functions for handling @username mentions

export interface Mention {
  username: string;
  startIndex: number;
  endIndex: number;
}

// Parse text to find all @username mentions
export function parseMentions(text: string): Mention[] {
  const mentionRegex = /@([a-zA-Z0-9_]+)/g;
  const mentions: Mention[] = [];
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push({
      username: match[1],
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }

  return mentions;
}

// Convert text with mentions to parts for rendering
export function textToMentionParts(text: string): Array<{ type: 'text' | 'mention'; content: string }> {
  const mentions = parseMentions(text);
  
  if (mentions.length === 0) {
    return [{ type: 'text', content: text }];
  }

  const parts: Array<{ type: 'text' | 'mention'; content: string }> = [];
  let lastIndex = 0;

  mentions.forEach((mention) => {
    // Add text before mention
    if (mention.startIndex > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, mention.startIndex),
      });
    }

    // Add mention
    parts.push({
      type: 'mention',
      content: mention.username,
    });

    lastIndex = mention.endIndex;
  });

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }

  return parts;
}

// Extract the current mention being typed (if any) from input text
export function getCurrentMention(text: string, cursorPosition: number): string | null {
  const textBeforeCursor = text.slice(0, cursorPosition);
  const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

  if (lastAtSymbol === -1) return null;

  const textAfterAt = textBeforeCursor.slice(lastAtSymbol + 1);
  
  // Check if there's a space after @ (which would mean the mention is complete)
  if (textAfterAt.includes(' ')) return null;

  return textAfterAt;
}
